import Backbone from "backbone";
import _ from "underscore";
import $ from "jquery";

import View from "backdraft-app/src/view";

import Modal from "shared/modal";

import containerTemplate from "./templates/modal_column_picker/container.jst";
import searchTemplate from "./templates/modal_column_picker/search.jst";
import itemTemplate from "./templates/modal_column_picker/item.jst";
import categoryTemplate from "./templates/modal_column_picker/category.jst";
import sectionTemplate from "./templates/modal_column_picker/section.jst";

import alertTemplate from "./templates/modal_column_picker/alert.jst";

const ColumnPicker = View.extend({
  template: containerTemplate,
  searchTemplate,
  itemTemplate,
  categoryTemplate,
  sectionTemplate,

  events: {
    "click a.select-all,a.deselect-all": "_changeSelection"
  },

  initialize(options) {
    options || (options = {});
    if (!options.actionTable) {
      throw new Error("ActionTable is required");
    }

    this.actionTable = options.actionTable;
    this.dataTable = options.actionTable.child("dataTable");
    this.columnsConfig = this.dataTable.columnsConfig().filter(columnConfig => !columnConfig.bulk && !columnConfig.hideFromEditColumns);
    this.columnPickerOptions = options.actionTable.columnPickerOptions || {};
    this.columnPickerOptions.sections = this.columnPickerOptions.sections || [{ id: 'default', name: '', categories: '*', opened: true }];

    // normalize the sections
    this.columnPickerOptions.sections = _.map(this.columnPickerOptions.sections, section => _.extend(section, { id: section.id || this._sanitizedCategorySelector(section.name) }));
    this.model = new Backbone.Model();
    this.will_change = new Backbone.Model();
  },

  render() {
    this.unstickit(this.model);
    this.stopListening(this.model);
    this.model.clear();
    this.changes = [];

    this.$el.html(this.template());
    this._renderColumnSelection(this.columnsConfig);

    if (this.columnPickerOptions.searchable) {
      this.$('div.columnPickerContainer').before(this.searchTemplate());
      this._renderWhenNoSearchQuery();
    }

    this.listenTo(this.model, "change", this._onModelChange);
    this._buildBindings(this.columnsConfig);
    return this;
  },

  restore() {
    const defaultColumns = this.actionTable.getDefaultDataTableSettings();
    _.each(defaultColumns, function(column, id) {
      if (!column.required && !column.notUserConfigurable) {
        this.model.set(id, {
          checked: column.checked,
          visible: this.model.get(id).visible
        });
      }
    }, this);
    this.restoreColumnSettings = true;
  },

  changeVisibleColumns() {
    const changes = {};
    _.each(this.will_change.attributes, (v, k) => {
      changes[k] = v.checked;
    }, this);
    this.actionTable.changeColumnVisibilities(changes, true);
    this.will_change.clear();
  },

  cancelChangeVisibleColumns() {
    this.will_change.clear();
  },

  _buildBindings(columnsConfig) {
    const bindings = {};

    _.each(columnsConfig, c => {
      if (c.id && !c.required) {
        bindings[`:checkbox[data-title='${_.escape(c.id)}']`] = {
          observe: c.id,
          update: '_checkboxBindingsUpdate',
          onSet: '_checkboxBindingsOnSet'
        };
      }
    }, this);
    if (this.columnPickerOptions.searchable) {
      bindings['input[name=column-picker-search]'] = 'search_query';
    }
    this.stickit(this.model, bindings);
  },

  _checkboxBindingsUpdate($el, val, column, options) {
    $el.prop("checked", val.checked);
    if (!val.visible) {
      $el.parents('.column-list-item').addClass('hidden');
      if (!$el.parents('.column-list-item').siblings(':not(.hidden)').length) {
        $el.parents('div.columnPicker--category').addClass('hidden');
      }
    } else {
      $el.parents('.column-list-item').removeClass('hidden');
      $el.parents('div.columnPicker--category').removeClass('hidden');
    }
  },

  _checkboxBindingsOnSet(checked, options) {
    return {
      checked,
      visible: this.model.get(options.observe).visible
    };
  },

  _onModelChange(object, options) {
    if (options && options.restore) { return; }

    if (_.has(this.model.changed, 'search_query')) {
      const query = this.model.changed.search_query.trim();
      this._searchColumns(query);
    }

    const changedColumns = _.omit(this.model.changed, 'search_query');
    _.each(changedColumns, function(v, k) {
      this.will_change.set(k, v);
    }, this);
  },

  // TODO: from TECH-1253 - Create a backdraft shared component that consolidates ActionTable's column picker and Webhook's substitution params picker - currently a lot of duplication!
  //  see https://github.com/Invoca/web/blob/72b683e36b72503327bb0f1ea9c7eb4aeb526133/backdraft/manage_webhooks/app/views/substitution_params_modal.js#L69-L166

  _renderColumnSelection(columnsConfig) {
    const selectorArea = this.$(".columnPickerContainer");
    selectorArea.empty();

    const visibleSections = _.select(this.columnPickerOptions.sections, section => // a section is visible if there is at least one column with a category that belongs in this section's config
      _.any(
        columnsConfig,
        c => section.categories === '*' || _.contains(section.categories, c.category)
      ));

    _.each(visibleSections, section => {
      selectorArea.append(this.sectionTemplate({
        section: section.name,
        sectionId: section.id,
        defaultOpen: section.opened
      }));
    });

    // hide title and ensure open if only one visible section
    if (visibleSections.length === 1) {
      this.$('.columnPicker--section').find('.toggle-content-trigger').hide();
      this.$('.columnPicker--section').find('.toggle-content').show();
    }

    // Hack so column picker masonry arrangement plays nice with this big ass column
    const separatedRingPoolColumns = _.partition(columnsConfig, c => _.result(c, "category") === "RingPool Details");
    const orderedColumns = separatedRingPoolColumns[1].concat(separatedRingPoolColumns[0]);

    _.each(orderedColumns, function(config) {
      if (config.id) {
        this.model.set(config.id, {
          checked: this.dataTable.columnVisibility(config.id),
          visible: true
        });

        if (!this._isNotVisibleAndNotUserConfigurable(config)) {
          this._renderColumnPickerListItem(visibleSections, selectorArea, config);
        }
      }
    }, this);
  },

  _isVisibleAndNotUserConfigurable(config) {
    return config.notUserConfigurable && this.dataTable.columnVisibility(config.attr);
  },

  _isNotVisibleAndNotUserConfigurable(config) {
    return config.notUserConfigurable && this.dataTable.columnVisibility(config.attr) === false;
  },

  _renderColumnPickerListItem(visibleSections, selectorArea, config) {
    let item;
    if (this.columnPickerOptions.categories) {
      item = this._renderColumnWithCategories(config, visibleSections);
    } else {
      selectorArea.addClass("withoutCategories row");
      item = this._renderColumn(config, selectorArea);
    }
    if (config.required || (this._isVisibleAndNotUserConfigurable(config))) {
      item.find(":checkbox").prop("checked", true).prop("disabled", true);
    }
  },

  _renderColumn(config, selectorArea) {
    if (!this.$(".column-list").length) {
      selectorArea.append($("<div class='col-sm-12'><div class='column-list'>"));
    }
    const list = this.$(".column-list");
    const item = $("<div class='column-list-item'>").html(this.itemTemplate({ title: config.title, id: config.id }));
    list.append(item);
    return item;
  },

  _renderColumnWithCategories(config, visibleSections) {
    const categoryId = this._sanitizedCategorySelector(config.category);

    const section = _.find(
      visibleSections,
      section => section.categories === '*' || _.contains(section.categories, config.category)
    );

    const domContainer = this.$(`#section_${section.id}`);

    if (!this.$(`#${categoryId}`).length) {
      domContainer.append(this.categoryTemplate({
        category: config.category,
        categoryId
      }));
    }

    const category = this.$(`#${categoryId}`);
    const item = $("<div class='column-list-item'>").html(this.itemTemplate({ title: config.title, id: config.id }));
    category.append(item);
    return item;
  },

  _searchColumns(query) {
    if (query === '') {
      this._renderWhenNoSearchQuery();
    } else {
      this.$('div.all-selectors').remove();
    }

    const escapeRegExp = str => // http://stackoverflow.com/a/2593661/37460
      str.replace(/[.?*+^$[\]\\(){}|-]/g, "\\$&");

    _.each(this.columnsConfig, function(column) {
      const queryRegex = new RegExp(escapeRegExp(query), "i"); // search for query case insensitive
      const match = column.title.search(queryRegex) > -1;
      const model = this.model.get(column.id);
      this.model.set(column.id, {
        checked: model.checked,
        visible: match
      });
    }, this);

    const sections = this.$('div.columnPicker--section');
    sections.find('.toggle-content-trigger').addClass('down');
    sections.find('.toggle-content').show();
  },

  _changeSelection(event) {
    const category = this.$(event.target).attr('name');
    let columns;
    if (category === 'all') {
      columns = this.columnsConfig;
    } else {
      columns = _.filter(this.columnsConfig, column => this._sanitizedCategorySelector(column.category) === category);
    }

    const checked = (this.$(event.target).attr('class') === 'select-all');
    _.each(columns, function(column) {
      if (!column.required && !column.notUserConfigurable) {
        this.model.set(column.id, {
          checked,
          visible: this.model.get(column.id).visible
        });
      }
    }, this);
  },

  _renderWhenNoSearchQuery() {
    const allSelectors = this.$('div.all-selectors');
    if (allSelectors.length) { return; }
    const searchBox = this.$('input[name=column-picker-search]');
    searchBox.after(`
        <div class="row all-selectors">
          <div class="col-sm-12">
            <a href="javascript:;" name="all" class="select-all">Select all columns</a>
             | 
            <a href="javascript:;" name="all" class="deselect-all">Deselect all columns</a>
          </div>
        </div>
      `);
  },

  _sanitizedCategorySelector(category) {
    return category.split(' ').join('-').replace(/[^A-z_-]/g, '');
  }

});

const ModalColumnPicker = Modal.extend({
  alertTemplate,

  events: {
    "click button.restore-default": "_onRestoreClick",
    "click button.btn-done": "_onDoneButtonClick"
  },

  className: "modal-column-picker",

  initialize(options) {
    options || (options = {});
    if (!options.actionTable) {
      throw new Error("ActionTable is required");
    }
    this.actionTable = options.actionTable;
  },

  title: "Select columns to display:",

  size: "large",

  body() {
    return new ColumnPicker({ actionTable: this.actionTable }).render();
  },

  buttons: [
    {
      text: "Restore default column settings",
      className: "btn-link restore-default mr-auto"
    },
    {
      text: "Cancel",
      className: "btn-secondary",
      close: true
    },
    {
      text: "Done",
      className: "btn-primary btn-done"
    }
  ],

  _onRestoreClick() {
    this.child("body").restore();
    return false;
  },

  _onDoneButtonClick() {
    this.$("div.alert").remove();
    if (_.where(this.child('body').model.attributes, { checked: true }).length > 0) {
      if (this.child("body").restoreColumnSettings) {
        this.actionTable.restoreDefaultColumnWidths();
      }
      this.child("body").changeVisibleColumns();
      $(window).trigger('resize');
      this.close();
      return true;
    } else {
      const alert = this.alertTemplate({
        title:    "There are no columns selected.",
        subtitle: "Please select a minimum of one column."
      });
      this.$('.modal-body').prepend(alert);
      return false;
    }
  }
});

export default ModalColumnPicker;
